import { Icon } from "../Icon/Icon";

type ButtonSize = "Large" | "Small";
type IconSize =
	| "Small"
	| "Medium"
	| "Large"
	| "XLarge"
	| "XXLarge"
	| "XXXLarge"
	| "XXXXLarge";
type ButtonType = "Primary" | "Secondary" | "Tertiary" | "TertiaryWhite";

interface IconButtonProps {
	size?: ButtonSize;
	iconSize?: IconSize;
	type?: ButtonType;
	disabled?: boolean;
	icon?: string;
	loadingIcon?: string;
	onClick?: () => void;
	loading?: boolean;
}

export const IconButton = ({
	size = "Large",
	iconSize = "Large",
	type = "Primary",
	disabled = false,
	icon = "plus_outline",
	loadingIcon = "",
	onClick,
	loading = false,
}: IconButtonProps) => {
	// Size configurations based on Figma design
	const sizeConfig: Record<ButtonSize, string> = {
		Large: "w-[56px] min-w-[56px] h-[56px]",
		Small: "w-[40px] min-w-[40px] h-[40px]",
	};

	// Base styles for all button types
	const baseClassName =
		"flex items-center justify-center rounded-lg transition-colors";

	// Type-specific styles
	const getTypeClass = () => {
		if (disabled) {
			switch (type) {
				case "Primary":
					return "bg-color_background_primary_2 text-white cursor-not-allowed";
				case "Secondary":
					return "bg-color-White text-color_text_grey_2 cursor-not-allowed";
				case "Tertiary":
					return "bg-transparent text-color_text_grey_3 cursor-not-allowed";
				case "TertiaryWhite":
					return "bg-transparent text-color_text_grey_1 cursor-not-allowed";
				default:
					return "bg-color_background_primary_2 text-white cursor-not-allowed";
			}
		} else {
			switch (type) {
				case "Primary":
					return "bg-color_background_primary_3 text-white hover:bg-color_background_primary_4 active:bg-color_background_primary_5";
				case "Secondary":
					return "bg-color-White text-color_text_grey_5 hover:bg-color_background_grey_1 active:bg-color_background_grey_2";
				case "Tertiary":
					return "bg-transparent text-color_text_grey_5 hover:text-color_text_grey_4 active:text-color_text_grey_3";
				case "TertiaryWhite":
					return "bg-transparent text-white hover:text-color_text_grey_1 active:text-white";
				default:
					return "bg-color_background_primary_3 text-white hover:bg-color_background_primary_4 active:bg-color_background_primary_5";
			}
		}
	};

	return (
		<button
			className={`${baseClassName} ${sizeConfig[size]} ${getTypeClass()}`}
			disabled={disabled || loading}
			onClick={onClick}
			aria-label={icon || "icon button"}
		>
			{icon && !loading && <Icon iconName={icon} size={iconSize} />}
			{loading && loadingIcon && (
				<Icon iconName={loadingIcon} size={iconSize} loading={loading} />
			)}
		</button>
	);
};
